// src/dev/types/inspector-types.ts
// Types for the Onboarding Inspector testing suite

import type { OnboardingStage } from "@/contexts/onboarding-context";

// Network log entry for tracking fetch requests
export interface NetworkLogEntry {
  id: string;
  url: string;
  tab: string | null; // Extracted from ?tab= param
  status: "pending" | "success" | "error";
  statusCode?: number;
  duration?: number; // ms
  rowCount?: number;
  responseSize?: number; // bytes
  error?: string;
  timestamp: number;
  responsePreview?: unknown; // First row or error details
}

// Onboarding stage override
export interface StageOverride {
  enabled: boolean;
  stage: OnboardingStage;
}

// Run state override (inferred states since web app can't see OnboardingStatusFlag)
export type RunState = "never" | "first" | "second" | "many";

export interface RunStateOverride {
  enabled: boolean;
  runState: RunState;
}

// Data completeness override
export type DataMode = "complete" | "partial" | "empty" | "error";

export interface DataOverride {
  enabled: boolean;
  mode: DataMode;
  missingTabs?: string[];
  errorTabs?: string[];
}

// User journey state override
export type JourneyState =
  | "new" // New user, first visit
  | "mid_exit" // Has temp_web_app_url in localStorage but no account
  | "free_with_data" // Signed up, completed onboarding, signed out
  | "signup_incomplete"; // pendingAccountSetup in Clerk but account not created

export interface JourneyOverride {
  enabled: boolean;
  state: JourneyState;
}

// All overrides combined
export interface InspectorOverrides {
  stage: StageOverride;
  runState: RunStateOverride;
  data: DataOverride;
  journey: JourneyOverride;
}

// Storage state for comparison
export interface LocalStorageState {
  temp_web_app_url?: string;
  temp_sheet_url?: string;
  temp_account_data?: string;
  onboarding_progress?: string;
  onboarding_last_step?: string;
}

export interface ClerkMetadataState {
  onboardingStage?: string;
  pendingAccountSetup?: {
    accountCID?: string;
    webAppUrl?: string;
    sheetUrl?: string;
    accountName?: string;
  };
  accounts?: Array<{
    accountCID: string;
    name: string;
    webAppUrl?: string;
  }>;
}

// Discrepancy detection
export interface StorageDiscrepancy {
  field: string;
  localStorage: string | undefined;
  clerk: string | undefined;
  message: string;
}

// Inspector context state
export interface InspectorState {
  isOpen: boolean;
  networkLogs: NetworkLogEntry[];
  overrides: InspectorOverrides;
  localStorageState: LocalStorageState;
  clerkMetadataState: ClerkMetadataState;
  discrepancies: StorageDiscrepancy[];
}

// Simulation mode - pretend to be a new user
export interface SimulationMode {
  enabled: boolean;
  simulatedEmail: string;
  // Intercepted Clerk metadata writes (what would have been saved)
  interceptedMetadata: Record<string, unknown>;
  // Track what localStorage keys have been written during simulation
  localStorageWrites: Record<string, string>;
}

// Storage keys for localStorage persistence
export const STORAGE_KEYS = {
  inspectorOpen: "dev_inspector_open",
  stageOverride: "dev_onboarding_stage_override",
  runStateOverride: "dev_run_state_override",
  dataOverride: "dev_data_override",
  journeyOverride: "dev_journey_override",
  simulationMode: "dev_simulation_mode",
  simulatedClerkMetadata: "dev_simulated_clerk_metadata",
  forceOnboarding: "dev_force_onboarding",
} as const;

// Utility to check if force onboarding mode is enabled (works outside React)
export function isForceOnboardingEnabled(): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  try {
    return localStorage.getItem(STORAGE_KEYS.forceOnboarding) === "true";
  } catch {
    return false;
  }
}

// Tab names that the web app fetches
export const ONBOARDING_TABS = [
  "account",
  "keyword80",
  "search80",
  "quality",
  "on_performance",
  "on_settings",
  "on_disapproved",
  "on_empty_ad_groups",
] as const;

export type OnboardingTab = (typeof ONBOARDING_TABS)[number];

// Utility function to check simulation mode from localStorage (works outside React)
export function isSimulationModeEnabled(): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.simulationMode);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.enabled === true;
    }
  } catch {
    // Ignore errors
  }
  return false;
}

// Utility to intercept Clerk metadata writes in simulation mode (works outside React)
export function interceptClerkWriteIfSimulating(
  metadata: Record<string, unknown>
): boolean {
  if (!isSimulationModeEnabled()) return false;

  try {
    // Get existing intercepted metadata
    const existingStr = localStorage.getItem(STORAGE_KEYS.simulatedClerkMetadata);
    const existing = existingStr ? JSON.parse(existingStr) : {};

    // Merge new metadata
    const updated = { ...existing, ...metadata };
    localStorage.setItem(STORAGE_KEYS.simulatedClerkMetadata, JSON.stringify(updated));

    console.log("[SIMULATION] Intercepted Clerk write:", metadata);
    return true;
  } catch (error) {
    console.error("[SIMULATION] Failed to intercept Clerk write:", error);
    return false;
  }
}
